/**
 * One file on the download page, and its Download button (mission 6 WP09
 * T049; FR-018, FR-026, FR-016, C-005; D.1, D.2, A.5, GEN.4, SEC.7, SEC.8;
 * sheet 30 callouts 3–5; research §1.14, §1.19).
 *
 * `FileRow` is presentational: the label, the register 13 description, the
 * facts (values in mono) and the action on the right. No `title` or `data-*`
 * attribute carries a value; `data-file-row` names the row's kind only.
 * A fact's exact form for assistive technology is sr-only text beside the
 * visible value, which is hidden from assistive technology; an `aria-label`
 * on a role-less span is not exposed (mission 8 WP10 T055; FR-020 A-L3; P4).
 *
 * `DownloadButton` goes through `lib/files.downloadObject`, which issues the
 * download, fetches the presigned URL at once and saves the bytes. The button
 * keeps a phase and nothing else, so the URL and the issuance body never reach
 * state, the DOM or an error (C-005, SEC.7). While busy the button is disabled,
 * so a second click issues nothing: each issuance writes an audit event (A.5).
 * An expired URL offers "Download again", which is a new issuance; a size
 * mismatch saves nothing and says so (register 12); a problem renders through
 * `ProblemBand` (FR-016), and a 503 `try-again` adds Retry. Anything else
 * renders the generic sentence, never the error's own text.
 */
import { useState, type ReactNode } from 'react'
import { Download } from 'lucide-react'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ApiError, type Problem } from '@/lib/api'
import { TransferError, downloadObject, type DownloadObject } from '@/lib/files'
import { Mono } from '@/lib/format'
import { PROBLEM } from '@/lib/problems'
import { copyText } from '@/lib/provisional-copy'

export interface FileFact {
  label: string
  value: string
  /** The exact form for assistive technology, e.g. the byte count beside a rounded size. */
  ariaLabel?: string
}

export interface FileRowProps {
  kind: 'generated' | 'upload' | 'findings'
  /** Absent for the findings row, whose action names it. */
  label?: ReactNode
  description: string
  facts?: FileFact[]
  action: ReactNode
  /** Mission 10 WP18 T107 (U31): content inside the card beneath the facts (the inline retention-0 warning). */
  note?: ReactNode
}

export function FileRow({ kind, label, description, facts = [], action, note }: FileRowProps) {
  return (
    <Card padding="compact" data-file-row={kind} className="flex items-start justify-between gap-6">
      <div className="min-w-0 flex-1">
        {label !== undefined ? <p className="mb-1 text-body font-medium text-navy-950">{label}</p> : null}
        <p className="text-body-sm text-navy-600">{description}</p>
        {facts.length > 0 ? (
          <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
            {facts.map((fact) => (
              <div key={fact.label} className="contents">
                <dt className="text-navy-600">{fact.label}</dt>
                <dd className="min-w-0 break-all text-navy-950">
                  {fact.ariaLabel === undefined ? (
                    <Mono>{fact.value}</Mono>
                  ) : (
                    <>
                      <Mono aria-hidden="true">{fact.value}</Mono>
                      <span className="sr-only">{fact.ariaLabel}</span>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {note !== undefined ? <div className="mt-3">{note}</div> : null}
      </div>
      <div className="shrink-0">{action}</div>
    </Card>
  )
}

type Phase = 'idle' | 'busy' | 'expired' | 'incomplete' | 'failed' | { problem: Problem }

function phaseFor(error: unknown): Phase {
  if (error instanceof TransferError && error.kind === 'expired') return 'expired'
  if (error instanceof TransferError && error.kind === 'incomplete') return 'incomplete'
  if (error instanceof ApiError) return { problem: error.problem }
  return 'failed'
}

export interface DownloadButtonProps {
  runId: string
  object: DownloadObject
  /** The region's one primary (R12): the generated file on Path A, the upload on Path B. */
  primary: boolean
}

export function DownloadButton({ runId, object, primary }: DownloadButtonProps) {
  const [phase, setPhase] = useState<Phase>('idle')

  async function issue() {
    setPhase('busy')
    try {
      await downloadObject(runId, object)
      setPhase('idle')
    } catch (error) {
      setPhase(phaseFor(error))
    }
  }

  const busy = phase === 'busy'
  const problem = typeof phase === 'object' ? phase.problem : null
  const retry = problem !== null && problem.type === PROBLEM.TRY_AGAIN && problem.status === 503
  return (
    <div className="flex max-w-md flex-col items-end gap-2">
      <Button
        variant={primary ? 'primary' : 'secondary'}
        loading={busy}
        disabled={busy}
        aria-busy={busy ? true : undefined}
        icon={<Download aria-hidden />}
        onClick={() => void issue()}
      >
        {phase === 'expired' ? 'Download again' : 'Download'}
      </Button>
      {phase === 'incomplete' ? <Band kind="blocked">{copyText('downloadIncomplete')}</Band> : null}
      {phase === 'failed' ? <Band kind="blocked">{copyText('genericError')}</Band> : null}
      {problem !== null ? <ProblemBand problem={problem} /> : null}
      {retry ? (
        <Button variant="secondary" size="sm" onClick={() => void issue()}>
          Retry
        </Button>
      ) : null}
    </div>
  )
}
